<script setup lang="ts">
import { computed, nextTick, ref, useId, watch } from 'vue'
import { AltArrowDownIcon, CheckIcon } from '@solar-icons/vue/linear'
import AppPopover from '../AppPopover/AppPopover.vue'
import type { SelectOption } from './select-types'
import { NEUTRAL_KEY, NEUTRAL_KEY_EDGE } from '../control-surface'
import { vPointerLight } from '../v-pointer-light'
import { vScrollFade } from '../v-scroll-fade'
import { useTravellingHighlight } from '@/composables/useTravellingHighlight'

const props = withDefaults(
  defineProps<{
    options: SelectOption[]
    modelValue: string
    disabled?: boolean
    /** Control size: `compact` (32px, small text, for selects inside a panel rim or row) · `md` (default) · `lg`. */
    size?: 'compact' | 'md' | 'lg'
    ariaLabel?: string
    /** Marks the trigger invalid; AppField passes this. */
    ariaInvalid?: boolean
    /** Id of the hint or error that describes the trigger; AppField passes this. */
    ariaDescribedby?: string
    id?: string
    placeholder?: string
  }>(),
  {
    disabled: false,
    size: 'md',
    ariaLabel: undefined,
    ariaInvalid: undefined,
    ariaDescribedby: undefined,
    id: undefined,
    placeholder: undefined,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const SIZE_SCALE = {
  compact: 'icon-sm h-16 px-6 text-body-sm-medium',
  md: 'h-(--field-height) px-6 text-body-medium',
  lg: 'px-6 py-6 text-body-medium',
} as const

const ROW_ARRIVAL_DELAY_MS = 40
const ROW_ARRIVAL_STAGGER_MS = 18
const ROW_ARRIVAL_LAST_STAGGERED = 8

function rowArrivalDelay(index: number): string {
  return `${ROW_ARRIVAL_DELAY_MS + Math.min(index, ROW_ARRIVAL_LAST_STAGGERED) * ROW_ARRIVAL_STAGGER_MS}ms`
}

const open = ref(false)
const triggerRef = ref<HTMLButtonElement | null>(null)
const listboxRef = ref<HTMLElement | null>(null)
const triggerWidth = ref(0)

const confirming = ref(false)

const uid = useId()
const listboxId = `${uid}-listbox`
function optionId(index: number): string {
  return `${uid}-option-${index}`
}

interface OptionSection {
  key: string
  heading?: string
  rows: { option: SelectOption; index: number }[]
}

const sections = computed<OptionSection[]>(() => {
  const result: OptionSection[] = []
  props.options.forEach((option, index) => {
    const current = result[result.length - 1]
    if (current && current.heading === option.group) {
      current.rows.push({ option, index })
      return
    }
    result.push({ key: `${uid}-group-${index}`, heading: option.group, rows: [{ option, index }] })
  })
  return result
})

const activeIndex = ref(-1)
const activeOptionId = computed(() =>
  activeIndex.value >= 0 ? optionId(activeIndex.value) : undefined,
)

const scrollRef = ref<HTMLElement | null>(null)
/** Travel stays off while the menu opens, so the highlight lands on its first row without sliding there. */
const {
  style: highlightStyle,
  travels: highlightSlides,
  measure: measureHighlight,
  enableTravel,
  disableTravel,
} = useTravellingHighlight({
  container: () => scrollRef.value,
  target: () => (activeIndex.value >= 0 ? rowElement(activeIndex.value) : null),
  track: () => activeIndex.value,
})

const selectedOption = computed(() =>
  props.options.find((option) => option.value === props.modelValue),
)

const rollDirection = ref(1)
watch(
  () => props.modelValue,
  (value, previous) => {
    const next = props.options.findIndex((option) => option.value === value)
    const before = props.options.findIndex((option) => option.value === previous)
    rollDirection.value = next >= before ? 1 : -1
  },
)

function isEnabled(option: SelectOption): boolean {
  return !option.disabled
}

function enabledIndexes(): number[] {
  const indexes: number[] = []
  props.options.forEach((option, index) => {
    if (isEnabled(option)) indexes.push(index)
  })
  return indexes
}

function firstEnabledIndex(): number {
  return enabledIndexes()[0] ?? -1
}

function selectedOrFirstIndex(): number {
  const selected = props.options.findIndex(
    (option) => option.value === props.modelValue && isEnabled(option),
  )
  return selected >= 0 ? selected : firstEnabledIndex()
}

function rowElement(index: number): HTMLElement | null {
  return listboxRef.value?.ownerDocument.getElementById(optionId(index)) ?? null
}

function setActive(index: number, scroll = true): void {
  if (index < 0 || confirming.value) return
  activeIndex.value = index
  if (scroll) scrollActiveIntoView()
}

function scrollActiveIntoView(): void {
  nextTick(() => {
    if (activeIndex.value < 0) return
    rowElement(activeIndex.value)?.scrollIntoView({ block: 'nearest' })
  })
}

function moveActive(direction: 1 | -1): void {
  const enabled = enabledIndexes()
  if (enabled.length === 0) return
  const pos = enabled.indexOf(activeIndex.value)
  const nextPos =
    pos === -1
      ? direction > 0
        ? 0
        : enabled.length - 1
      : (pos + direction + enabled.length) % enabled.length
  setActive(enabled[nextPos]!)
}

function openMenu(): void {
  if (props.disabled) return
  open.value = true
}

function closeMenu(): void {
  open.value = false
}

function commit(index: number): void {
  const option = props.options[index]
  if (!option || option.disabled || confirming.value) return
  activeIndex.value = index
  emit('update:modelValue', option.value)
  confirming.value = true
}

function onHighlightAnimationEnd(): void {
  if (!confirming.value) return
  confirming.value = false
  open.value = false
  nextTick(() => triggerRef.value?.focus())
}

let typeBuffer = ''
let typeTimer: ReturnType<typeof setTimeout> | undefined
function typeahead(char: string): void {
  if (typeTimer) clearTimeout(typeTimer)
  typeBuffer += char.toLowerCase()
  typeTimer = setTimeout(() => {
    typeBuffer = ''
  }, 500)

  const enabled = enabledIndexes()
  if (enabled.length === 0) return
  const from = enabled.indexOf(activeIndex.value)
  const ordered = [...enabled.slice(from + 1), ...enabled.slice(0, from + 1)]
  const match = ordered.find((index) =>
    props.options[index]!.label.toLowerCase().startsWith(typeBuffer),
  )
  if (match !== undefined) setActive(match)
}

const FOCUSABLE_SELECTOR =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'

function focusAdjacent(direction: 1 | -1): void {
  const trigger = triggerRef.value
  if (!trigger || typeof document === 'undefined') return
  const all = Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.getClientRects().length > 0,
  )
  const index = all.indexOf(trigger)
  if (index === -1) return
  all[index + direction]?.focus()
}

function isPrintable(event: KeyboardEvent): boolean {
  return event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey
}

function onTriggerKeydown(event: KeyboardEvent): void {
  if (props.disabled) return

  if (!open.value) {
    if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp' ||
      event.key === 'Enter' ||
      event.key === ' '
    ) {
      event.preventDefault()
      openMenu()
    }
    return
  }

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      moveActive(1)
      break
    case 'ArrowUp':
      event.preventDefault()
      if (event.altKey) {
        closeMenu()
        break
      }
      moveActive(-1)
      break
    case 'Home':
      event.preventDefault()
      setActive(firstEnabledIndex())
      break
    case 'End': {
      event.preventDefault()
      const enabled = enabledIndexes()
      setActive(enabled[enabled.length - 1] ?? -1)
      break
    }
    case 'Enter':
    case ' ':
      event.preventDefault()
      commit(activeIndex.value)
      break
    case 'Escape':
      event.preventDefault()
      closeMenu()
      break
    case 'Tab': {
      const direction = event.shiftKey ? -1 : 1
      event.preventDefault()
      closeMenu()
      nextTick(() => focusAdjacent(direction))
      break
    }
    default:
      if (isPrintable(event)) {
        event.preventDefault()
        typeahead(event.key)
      }
  }
}

function onOpened(): void {
  activeIndex.value = selectedOrFirstIndex()
  triggerWidth.value = triggerRef.value?.getBoundingClientRect().width ?? 0
  scrollActiveIntoView()
  nextTick(() => {
    measureHighlight()
    requestAnimationFrame(() => {
      enableTravel()
    })
  })
}

function onClosed(): void {
  activeIndex.value = -1
  typeBuffer = ''
  confirming.value = false
  disableTravel()
}
</script>

<template>
  <AppPopover
    v-model:open="open"
    role="presentation"
    aria-haspopup="listbox"
    placement="bottom-start"
    :trap-focus="false"
    :auto-focus="false"
    @opened="onOpened"
    @closed="onClosed"
  >
    <template #trigger>
      <button
        :id="id"
        ref="triggerRef"
        v-pointer-light
        type="button"
        role="combobox"
        :disabled="disabled"
        :aria-label="ariaLabel"
        :aria-invalid="ariaInvalid || undefined"
        :aria-describedby="ariaDescribedby"
        aria-haspopup="listbox"
        :aria-expanded="open"
        :aria-controls="listboxId"
        :aria-activedescendant="open ? activeOptionId : undefined"
        :class="[
          'control-press press-wide pointer-glow flex w-full items-center justify-between gap-6 rounded-6 text-left shadow-contact',
          'outline-none',
          NEUTRAL_KEY,
          open
            ? 'inset-ring-accent-blue ring-4 ring-halo-blue'
            : ariaInvalid
              ? 'inset-ring-accent-red ring-4 ring-halo-red'
              : `${NEUTRAL_KEY_EDGE} focus-visible:inset-ring-accent-blue focus-visible:ring-4 focus-visible:ring-halo-blue`,
          SIZE_SCALE[size],
          disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
        ]"
        @keydown="onTriggerKeydown"
      >
        <span class="grid min-w-0 overflow-hidden" :style="{ '--roll-direction': rollDirection }">
          <Transition name="value-roll">
            <span
              v-if="selectedOption"
              :key="selectedOption.value"
              class="col-start-1 row-start-1 min-w-0 truncate text-text-primary"
              :style="selectedOption.style"
              >{{ selectedOption.label }}</span
            >
            <span
              v-else
              key="placeholder"
              class="col-start-1 row-start-1 min-w-0 truncate text-text-tertiary"
              >{{ placeholder }}</span
            >
          </Transition>
        </span>
        <AltArrowDownIcon
          :class="[
            'shrink-0 text-text-tertiary transition-transform duration-slow ease-spring-bouncy',
            open ? 'rotate-180' : '',
          ]"
        />
      </button>
    </template>

    <div
      ref="scrollRef"
      v-scroll-fade
      class="relative isolate max-h-[60vh] overflow-y-auto p-2"
      :style="{ minWidth: `${triggerWidth}px` }"
    >
      <span
        aria-hidden="true"
        :class="[
          'pointer-events-none absolute inset-x-2 top-0 -z-1 rounded-2 bg-accent-blue',
          highlightSlides ? 'travelling-highlight' : '',
          activeIndex >= 0 ? 'opacity-100' : 'opacity-0',
          confirming ? 'animate-blink' : '',
        ]"
        :style="highlightStyle"
        @animationend="onHighlightAnimationEnd"
      />
      <ul
        :id="listboxId"
        ref="listboxRef"
        role="listbox"
        :aria-label="ariaLabel"
        :aria-activedescendant="activeOptionId"
        tabindex="-1"
        class="outline-none"
      >
        <li v-for="section in sections" :key="section.key" role="presentation">
          <span
            v-if="section.heading !== undefined"
            :id="section.key"
            class="block px-6 pt-4 pb-2 text-caption-medium text-text-tertiary select-none"
            >{{ section.heading }}</span
          >
          <ul
            :role="section.heading !== undefined ? 'group' : 'presentation'"
            :aria-labelledby="section.heading !== undefined ? section.key : undefined"
          >
            <li
              v-for="{ option, index } in section.rows"
              :id="optionId(index)"
              :key="option.value"
              role="option"
              :aria-selected="option.value === modelValue"
              :aria-disabled="option.disabled || undefined"
              :style="{ animationDelay: rowArrivalDelay(index) }"
              :class="[
                'flex items-center justify-between gap-6 rounded-2 px-6 py-4 select-none motion-safe:animate-row-in',
                'transition-colors duration-fast ease-standard',
                option.value === modelValue ? 'text-body-medium' : 'text-body',
                option.disabled
                  ? 'cursor-not-allowed text-text-tertiary opacity-40'
                  : index === activeIndex
                    ? 'cursor-pointer text-text-on-accent'
                    : 'cursor-pointer text-text-primary',
              ]"
              @click="!option.disabled && commit(index)"
              @mousemove="!option.disabled && setActive(index, false)"
            >
              <span class="min-w-0 truncate" :style="option.style">{{ option.label }}</span>
              <CheckIcon
                v-if="option.value === modelValue"
                :class="[
                  'shrink-0',
                  index === activeIndex ? 'text-text-on-accent' : 'text-accent-blue',
                ]"
              />
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </AppPopover>
</template>
